// Редовете на инвентаризацията като карти (под десктоп ширина) — същата „тиха"
// подредба като решетката: име · „Търсено: 48 бр", полето „Намерено" и
// разликата (празно, докато редът не е преброен). При излишък — Ед. цена
// (червена и задължителна без източник на цена) и Стойност (нето); при липса —
// ред само за четене „Ед. цена · Стойност". Партида/срок — сами само за
// продукт, който ги следи, иначе връзката „+ партида / срок" (`batchStateOf`).
// Трите блока носят същите ленти-заглавия като решетката (`StocktakeBlockBand`).
// Полетата носят СЪЩИТЕ id-та (`stx-{продукт}-{кол}`) като клетките на
// решетката, затова търсачката и стрелките работят еднакво; липсващите клетки
// стрелките прескачат. Над блоковете — картите на приготвените рецепти
// (StocktakeRecipeCards); продукт в тях носи чипа „+0,630 кг в рецепти".
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  FLAT_CELL_ERROR_CLASS,
  STOCKTAKE_COLS,
  batchFieldId,
  cellId,
  differenceTextOf,
  differenceToneOf,
  formatQtyWithUnit,
  signedValueInputOf,
  type StocktakeComputedRow,
} from "./stocktakeWorksheetShared";
import { formatUnitPrice } from "../../components/numbers";
import type { NumericCol } from "./useStocktakeRowsActions";
import type { StocktakeRowsViewProps } from "./StocktakeWorksheetTable";
import { StocktakeBlockBand } from "./StocktakeBlockBand";
import { batchStateOf, type StocktakeBlockKey } from "./stocktakeView";
import { formatSignedMoney } from "./stocktakeMeta";
import { StocktakeRecipeCards } from "./StocktakeRecipeSection";
import { CountLooseHint, InRecipesBreakdown, InRecipesChip } from "./StocktakeInRecipes";

// Полетата на картата — с рамка (докосване с пръст), 16px срещу зуума на iOS
const FIELD_CLASS =
  "box-border w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-2.5 text-base leading-[normal] text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";

export const StocktakeLineCards = ({
  blocks,
  recipes,
  substitutionProductIds,
  appliedProductIds,
  onCellChange,
  onCellBlur,
  onBatchChange,
  onExpiryChange,
  batchOpenIds,
  onOpenBatch,
  recipeBreakdownIds,
  onToggleRecipeBreakdown,
}: StocktakeRowsViewProps) => {
  const { t } = useTranslation();

  const label = (text: string) => (
    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">{text}</span>
  );
  const numberField = (row: StocktakeComputedRow, col: NumericCol, value: string) => {
    const colIndex = STOCKTAKE_COLS[col];
    const priceRequired = col === "price" && row.priceMissing;
    return (
      <input
        id={cellId(row.product.id, colIndex)}
        data-rk={row.product.id}
        data-c={colIndex}
        inputMode="decimal"
        enterKeyHint="next"
        value={value}
        placeholder={priceRequired ? t("inventoryPage.stkPricePh") : col === "net" ? "—" : undefined}
        aria-invalid={priceRequired ? true : undefined}
        onChange={(e) => onCellChange(row.product.id, col, e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => onCellBlur(row.product.id, col)}
        className={`${FIELD_CLASS} text-right font-sheet ${
          priceRequired
            ? FLAT_CELL_ERROR_CLASS
            : col === "price" && row.priceAuto
              ? "italic text-muted"
              : col === "net"
                ? "font-semibold !text-ok-deep"
                : "font-semibold"
        }`}
      />
    );
  };

  const renderCard = (row: StocktakeComputedRow, rowNumber: number, block: StocktakeBlockKey) => {
    const product = row.product;
    const isSurplus = row.kind === "surplus";
    const batchState = batchStateOf(row, batchOpenIds);
    const batchError = isSurplus && !!product.trackBatch && !row.draft.batch.trim();
    const expiryError = isSurplus && !!product.trackExpiry && !row.draft.expiry;
    const difference = differenceTextOf(row);
    return (
      <div
        key={product.id}
        className={`flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3 ${
          block === "counted" ? "bg-panel" : ""
        }`}
      >
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-center gap-2 font-semibold text-ink">
              <span className="min-w-0 truncate">
                {rowNumber}. {product.name}
              </span>
              {substitutionProductIds.has(product.id) && (
                <span
                  title={t("inventoryPage.subChipHint")}
                  className={`shrink-0 rounded-full px-[7px] pb-[2px] pt-px text-[10px] leading-[normal] ${
                    appliedProductIds.has(product.id)
                      ? "bg-brand text-white"
                      : "bg-brand-tint text-brand-deep-ink"
                  }`}
                >
                  ⇄
                </span>
              )}
            </span>
            <span className="text-xs text-muted">
              {t("inventoryPage.colSystem")}: {formatQtyWithUnit(row.systemQty, row.decimals, row.unit)}
            </span>
            {row.needsLooseCount && <CountLooseHint />}
          </div>
          {difference && (
            <span className={`shrink-0 font-sheet text-sm font-semibold ${differenceToneOf(row)}`}>
              {difference}
            </span>
          )}
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">
          <label className="flex flex-col gap-1">
            {label(`${t("inventoryPage.colCounted")}${row.unit ? ` · ${row.unit}` : ""}`)}
            {numberField(row, "counted", row.draft.counted)}
          </label>
          {isSurplus && (
            <>
              <label className="flex flex-col gap-1">
                {label(`${t("inventoryPage.colUnitPrice")}${row.priceMissing ? " *" : ""}`)}
                {numberField(row, "price", row.priceInput)}
              </label>
              <label className="flex flex-col gap-1">
                {label(t("inventoryPage.colValue"))}
                {numberField(row, "net", signedValueInputOf(row))}
              </label>
            </>
          )}
        </div>
        {/* „Намерено" е насипното; частта в приготвените рецепти — чип + разбивка */}
        {row.inRecipesQty > 0 && (
          <InRecipesChip
            row={row}
            large
            open={recipeBreakdownIds.has(product.id)}
            onToggle={() => onToggleRecipeBreakdown(product.id)}
          />
        )}
        {row.inRecipesQty > 0 && recipeBreakdownIds.has(product.id) && (
          <InRecipesBreakdown row={row} className="" />
        )}
        {/* Липса: цената и стойността са само за четене (FIFO цена) */}
        {row.kind === "shortage" && difference && (
          <div className="flex flex-wrap justify-end gap-x-2.5 gap-y-1 text-[12.5px] text-body">
            <span>
              {t("inventoryPage.colUnitPrice")}{" "}
              <span className="font-sheet text-ink">{formatUnitPrice(row.unitPrice)}</span>
            </span>
            <span>
              · {t("inventoryPage.colValue")}{" "}
              <span className="font-sheet font-semibold text-danger-ink">
                {formatSignedMoney(row.differenceNet)}
              </span>
            </span>
          </div>
        )}
        {/* Излишък на продукт без следене: партидата/срокът — само при нужда */}
        {batchState === "link" && (
          <button
            type="button"
            onClick={() => onOpenBatch(product.id)}
            className="cursor-pointer self-start border-none bg-transparent p-0 text-[13px] font-semibold leading-[normal] text-brand-deep-ink"
          >
            {t("inventoryPage.stqBatchLink")}
          </button>
        )}
        {batchState === "fields" && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
            <label className="flex flex-col gap-1">
              {label(`${t("inventoryPage.colBatch")}${product.trackBatch ? " *" : ""}`)}
              <input
                id={batchFieldId(product.id)}
                value={row.draft.batch}
                onChange={(e) => onBatchChange(product.id, e.target.value)}
                className={`${FIELD_CLASS} ${batchError ? FLAT_CELL_ERROR_CLASS : ""}`}
              />
            </label>
            <label className="flex flex-col gap-1">
              {label(`${t("inventoryPage.colExpiry")}${product.trackExpiry ? " *" : ""}`)}
              <DatePicker
                inputReadOnly
                value={row.draft.expiry ? dayjs(row.draft.expiry) : null}
                onChange={(d) => onExpiryChange(product.id, d ? d.format("YYYY-MM-DD") : "")}
                format="DD.MM.YYYY"
                status={expiryError ? "error" : undefined}
                className="!h-[44px] w-full !rounded-lg"
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  // Лентите и картите са ЕДИН плосък списък с ключ по продукт — картата, която
  // минава в „Преброени", запазва DOM възела и фокуса си (виж решетката)
  return (
    <div>
      {recipes && recipes.rows.length > 0 && <StocktakeRecipeCards {...recipes} />}
      {blocks.flatMap((block) => [
        <StocktakeBlockBand
          key={`band-${block.key}`}
          kind={block.key}
          count={block.count}
          total={block.total}
          pinnedFrom={block.pinnedFrom}
        />,
        ...block.rows.map((row, rowIndex) => renderCard(row, block.startNumber + rowIndex, block.key)),
      ])}
    </div>
  );
};

export default StocktakeLineCards;
